import React, { useEffect, useState } from 'react';
import {
  getMailboxes, addMailbox, testMailbox, updateMailbox, applyMailboxRule, deleteMailbox,
  refreshMailboxes, cleanMailbox, cleanMailboxes, getEmailCleanup, saveEmailCleanup, getEmailRuns,
} from '../api';
import {
  Mail, Plus, RefreshCw, Trash2, Zap, Save, Play, AlertTriangle, CheckCircle2, Clock, X, Globe,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
/* Mirrors backend/mailApi.js PRESETS — anything else is typed in by hand.
   Only the hostname differs between providers; the cleanup itself is plain
   IMAP and does not care who runs the mailbox. */
const HOST_PRESETS = [
  { key: 'hostinger', name: 'Hostinger', host: 'imap.hostinger.com', port: 993 },
  { key: 'titan',     name: 'Titan',     host: 'imap.titan.email',   port: 993 },
  { key: 'zoho',      name: 'Zoho',      host: 'imap.zoho.com',      port: 993 },
];

const SOURCE_FOLDERS = [
  { key: 'inbox',  label: 'Inbox' },
  { key: 'junk',   label: 'Junk' },
  { key: 'sent',   label: 'Sent' },
  { key: 'drafts', label: 'Drafts' },
];

const nf = (n) => (n || 0).toLocaleString();
const when = (d) => (d ? new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

function Toggle({ on, onClick, label }) {
  return <button type="button" className={`switch${on ? ' on' : ''}`} onClick={onClick} aria-pressed={on} aria-label={label} />;
}

/* One folder count per mailbox row, so the table stays readable. */
function folderCount(mb, role) {
  const f = (mb.folder_stats || []).find(x => x.folder === role);
  return f ? f.total : 0;
}

/* A site normally runs several mailboxes (info@, sales@, support@…), so the
   table is grouped by domain with a subtotal per group rather than listing
   two dozen addresses flat. */
function groupByDomain(boxes) {
  const groups = [];
  for (const mb of boxes) {
    const domain = mb.domain || String(mb.email).split('@').pop();
    let g = groups.find(x => x.domain === domain);
    if (!g) {
      g = { domain, site_name: mb.site_name || null, boxes: [], total: 0, to_trash: 0, to_purge: 0, active: 0 };
      groups.push(g);
    }
    if (!g.site_name && mb.site_name) g.site_name = mb.site_name;
    g.boxes.push(mb);
    g.total += mb.total || 0;
    if (mb.auto_cleanup) {
      g.to_trash += mb.to_trash || 0;
      g.to_purge += mb.to_purge || 0;
      g.active++;
    }
  }
  return groups;
}

export default function EmailCleanup() {
  const [boxes, setBoxes]     = useState([]);
  const [cfg, setCfg]         = useState(null);
  const [runs, setRuns]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState({});        // id | 'all' | 'refresh' -> bool
  const [adding, setAdding]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [lastDomain, setLastDomain] = useState(null); // keeps the next address one field away
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    label: '', email: '', password: '',
    imap_host: 'imap.hostinger.com', imap_port: 993, imap_secure: true,
  });
  // The rule card edits one set of values and applies it to every mailbox.
  const [rule, setRule] = useState({ retention_days: 7, trash_days: 3, folders: ['inbox', 'junk'], keep_flagged: true });
  const [savingRule, setSavingRule] = useState(false);

  const load = async () => {
    try {
      const list = await getMailboxes();
      setBoxes(list);
      if (list.length) {
        const first = list[0];
        setRule({
          retention_days: first.retention_days,
          trash_days: first.trash_days,
          folders: String(first.folders || 'inbox,junk').split(',').filter(Boolean),
          keep_flagged: first.keep_flagged,
        });
      }
    } catch (err) { toast.error(err?.error || 'Could not load mailboxes'); }
  };

  useEffect(() => {
    Promise.all([
      load(),
      getEmailCleanup().then(setCfg).catch(() => {}),
      getEmailRuns(10).then(setRuns).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const totals = boxes.reduce((t, b) => ({
    stored: t.stored + (b.total || 0),
    toTrash: t.toTrash + (b.auto_cleanup ? (b.to_trash || 0) : 0),
    toPurge: t.toPurge + (b.auto_cleanup ? (b.to_purge || 0) : 0),
    active: t.active + (b.auto_cleanup ? 1 : 0),
  }), { stored: 0, toTrash: 0, toPurge: 0, active: 0 });

  const mark = (key, val) => setBusy(b => ({ ...b, [key]: val }));

  const handleTest = async () => {
    if (!form.email || !form.password) return toast.error('Email and password are required');
    setTestResult(null);
    mark('test', true);
    try {
      const r = await testMailbox(form);
      setTestResult(r);
      toast.success(`Connected — ${r.folders.length} folder(s) found`);
    } catch (err) {
      setTestResult({ ok: false, error: err?.error || 'Connection failed' });
      toast.error(err?.error || 'Connection failed');
    } finally { mark('test', false); }
  };

  /* A site usually has several mailboxes, so adding one leaves the form open
     with the domain and server details kept — the next address is two fields
     away instead of a full re-entry. */
  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const saved = await addMailbox({ ...form, ...rule, folders: rule.folders.join(','), auto_cleanup: false });
      toast.success(
        saved.site_name
          ? `${saved.email} connected to ${saved.site_name} — counting in the background`
          : `${saved.email} connected — counting in the background`
      );
      setLastDomain(String(form.email).split('@').pop());
      setForm(f => ({ ...f, label: '', email: '', password: '' }));
      setTestResult(null);
      await load();
    } catch (err) { toast.error(err?.error || 'Could not connect the mailbox'); }
    finally { setAdding(false); }
  };

  const handleRefreshAll = async () => {
    mark('refresh', true);
    try {
      const r = await refreshMailboxes();
      const failed = (r.results || []).filter(x => !x.ok).length;
      if (failed) toast.error(`${failed} of ${r.mailboxes} mailbox(es) could not be read`);
      else toast.success(`Counts refreshed for ${r.mailboxes} mailbox(es)`);
      await load();
    } catch (err) { toast.error(err?.error || 'Refresh failed'); }
    finally { mark('refresh', false); }
  };

  const toggleAuto = async (mb) => {
    const next = !mb.auto_cleanup;
    setBoxes(bs => bs.map(b => (b.id === mb.id ? { ...b, auto_cleanup: next } : b))); // optimistic
    try { await updateMailbox(mb.id, { auto_cleanup: next }); }
    catch (err) {
      setBoxes(bs => bs.map(b => (b.id === mb.id ? { ...b, auto_cleanup: !next } : b)));
      toast.error(err?.error || 'Could not save');
    }
  };

  const handleDelete = async (mb) => {
    if (!window.confirm(`Disconnect ${mb.email}?\n\nThe mailbox itself is untouched — only its saved credentials and counts are removed from this dashboard.`)) return;
    try { await deleteMailbox(mb.id); toast.success('Mailbox disconnected'); await load(); }
    catch (err) { toast.error(err?.error || 'Could not disconnect'); }
  };

  const saveRule = async () => {
    setSavingRule(true);
    try {
      await applyMailboxRule({ ...rule, folders: rule.folders.join(',') });
      toast.success(`Rule applied to all ${boxes.length} mailbox(es)`);
      await load();
    } catch (err) { toast.error(err?.error || 'Could not save the rule'); }
    finally { setSavingRule(false); }
  };

  const saveSchedule = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next); // optimistic
    try { setCfg(await saveEmailCleanup(patch)); }
    catch (err) { toast.error(err?.error || 'Could not save the schedule'); setCfg(cfg); }
  };

  const toggleFolder = (key) => setRule(r => ({
    ...r,
    folders: r.folders.includes(key) ? r.folders.filter(f => f !== key) : [...r.folders, key],
  }));

  /* Both cleanup entry points go through a dry run first, so the numbers in
     the confirmation are the server's, not a guess from stale counts. */
  /* `target` is one mailbox, a domain group, or null for everything. */
  const runCleanup = async (target) => {
    const group = target && target.domain ? target : null;
    const mb = target && !group ? target : null;
    const key = mb ? mb.id : (group ? `g:${group.domain}` : 'all');
    const scope = mb ? mb.email
      : group ? `${group.active} mailbox(es) on ${group.domain}`
      : `${totals.active} mailbox(es)`;
    const ids = group ? group.boxes.filter(b => b.auto_cleanup).map(b => b.id) : null;

    mark(key, true);
    try {
      const preview = mb
        ? await cleanMailbox(mb.id, { dry_run: true })
        : await cleanMailboxes(ids ? { dry_run: true, account_ids: ids } : { dry_run: true });

      // Stage 2 stays quiet until mail has actually served its grace period,
      // so say that rather than leaving the run looking like a no-op.
      const note = (preview.results || []).map(r => r.purge_note).find(Boolean);
      if (!preview.moved && !preview.purged) {
        toast(note ? `Nothing to clean in ${scope} — ${note}` : `Nothing to clean in ${scope}`,
          { duration: note ? 8000 : 4000 });
        return;
      }
      const ok = window.confirm(
        `Clean ${scope}?\n\n` +
        `Step 1 — move ${nf(preview.moved)} email(s) older than ${rule.retention_days} days to Trash (restorable).\n` +
        `Step 2 — permanently delete ${nf(preview.purged)} email(s) that have already served their ${rule.trash_days}-day grace period in Trash.\n` +
        (preview.purged ? '' : `         (${note || 'nothing is old enough to purge yet'})\n`) +
        `\nAnything moved in step 1 stays in Trash for ${rule.trash_days} days first. Step 2 cannot be undone.`
      );
      if (!ok) return;

      const r = mb ? await cleanMailbox(mb.id, {}) : await cleanMailboxes(ids ? { account_ids: ids } : {});
      const failed = (r.results || []).filter(x => !x.success);
      toast.success(`${nf(r.moved)} moved to Trash · ${nf(r.purged)} permanently removed`, { duration: 7000 });
      if (!r.purged && note) toast(note, { duration: 8000 });
      if (failed.length) toast.error(`${failed.length} mailbox(es) failed: ${failed.map(f => f.error).join(' · ')}`, { duration: 10000 });
      await load();
      getEmailRuns(10).then(setRuns).catch(() => {});
    } catch (err) { toast.error(err?.error || 'Cleanup failed'); }
    finally { mark(key, false); }
  };

  const statusBadge = (mb) => {
    if (mb.status === 'auth_failed') return <span className="badge red" title={mb.status_note || ''}>sign-in failed</span>;
    if (mb.status === 'unreachable') return <span className="badge amber" title={mb.status_note || ''}>unreachable</span>;
    if (mb.status === 'ok') return <span className="badge green">ok</span>;
    return <span className="badge gray">not checked</span>;
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Email Cleanup</h1>
          <p>
            {boxes.length} mailbox{boxes.length !== 1 ? 'es' : ''} · {nf(totals.stored)} emails stored ·
            {' '}mail moves to Trash after {rule.retention_days} days, Trash is emptied after {rule.trash_days} more
          </p>
        </div>
        <div className="header-actions">
          <Button className="btn" onClick={handleRefreshAll} loading={busy.refresh} disabled={!boxes.length}>
            <RefreshCw size={13}/> Refresh counts
          </Button>
          <Button className="btn primary" onClick={() => setShowForm(s => !s)}>
            {showForm ? <X size={13}/> : <Plus size={13}/>} {showForm ? 'Close' : 'Connect mailbox'}
          </Button>
        </div>
      </div>

      <div className="page-content">

        {/* Stat cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-top"><div className="stat-icon"><Mail size={18}/></div></div>
            <div className="stat-label">Mailboxes</div>
            <div className="stat-val">{boxes.length}</div>
            <div className="stat-sub">{totals.active} with auto-clean on</div>
          </div>
          <div className="stat-card">
            <div className="stat-top"><div className="stat-icon"><Mail size={18}/></div></div>
            <div className="stat-label">Emails stored</div>
            <div className="stat-val blue">{nf(totals.stored)}</div>
            <div className="stat-sub">Inbox + Junk + Trash</div>
          </div>
          <div className="stat-card">
            <div className="stat-top"><div className="stat-icon amber"><Clock size={18}/></div></div>
            <div className="stat-label">Step 1 · to Trash</div>
            <div className="stat-val amber">{nf(totals.toTrash)}</div>
            <div className="stat-sub">Older than {rule.retention_days} days · restorable</div>
          </div>
          <div className="stat-card">
            <div className="stat-top"><div className="stat-icon"><Trash2 size={18}/></div></div>
            <div className="stat-label">Step 2 · purge</div>
            <div className="stat-val">{nf(totals.toPurge)}</div>
            <div className="stat-sub">In Trash past the grace period · permanent</div>
          </div>
        </div>

        {/* Connect form */}
        {showForm && (
          <div className="card">
            <div className="card-head">
              <h3><Plus size={14}/> Connect a mailbox</h3>
              {/* Only the hostname differs between providers — the cleanup is
                  plain IMAP either way. */}
              <div className="seg">
                {HOST_PRESETS.map(p => (
                  <button key={p.key} type="button"
                    className={form.imap_host === p.host ? 'active' : ''}
                    onClick={() => setForm(f => ({ ...f, imap_host: p.host, imap_port: p.port, imap_secure: true }))}>
                    {p.name}
                  </button>
                ))}
                <button type="button"
                  className={HOST_PRESETS.some(p => p.host === form.imap_host) ? '' : 'active'}
                  onClick={() => setForm(f => ({ ...f, imap_host: '' }))}>
                  Custom
                </button>
              </div>
            </div>
            <div style={{ padding: '12px 16px' }}>
              <div style={{ background:'var(--accent-bg)', border:'1px solid var(--accent2)', borderRadius:8, padding:'10px 12px', fontSize:11, color:'var(--accent-text)', marginBottom:12 }}>
                Pick your provider above, or type the IMAP host for anything else. The password is stored
                encrypted with the same key that protects your site tokens, and is never shown again.
                A mailbox with two-factor auth needs an app password.
              </div>

              <form onSubmit={handleAdd}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div className="field" style={{ margin:0 }}>
                    <label htmlFor="mb-label">Label</label>
                    <input id="mb-label" type="text" placeholder="Tree Service MD — info"
                      value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}/>
                  </div>
                  <div className="field" style={{ margin:0 }}>
                    <label htmlFor="mb-email">Email address *</label>
                    <input id="mb-email" type="email" required
                      placeholder={lastDomain ? `sales@${lastDomain}` : 'info@yourdomain.com'}
                      value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}/>
                  </div>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr', gap:12, marginTop:12 }}>
                  <div className="field" style={{ margin:0 }}>
                    <label htmlFor="mb-pass">Mailbox password *</label>
                    <input id="mb-pass" type="password" placeholder="••••••••" required autoComplete="new-password"
                      value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}/>
                  </div>
                  <div className="field" style={{ margin:0 }}>
                    <label htmlFor="mb-host">IMAP host</label>
                    <input id="mb-host" type="text" value={form.imap_host}
                      onChange={e => setForm({ ...form, imap_host: e.target.value })}/>
                  </div>
                  <div className="field" style={{ margin:0 }}>
                    <label htmlFor="mb-port">Port</label>
                    <input id="mb-port" type="number" value={form.imap_port}
                      onChange={e => setForm({ ...form, imap_port: e.target.value })}/>
                  </div>
                </div>

                {testResult && (
                  <div style={{
                    marginTop:12, padding:'10px 12px', borderRadius:10, fontSize:11.5,
                    background: testResult.ok ? 'var(--green-bg)' : 'var(--red-bg)',
                    color: testResult.ok ? 'var(--green-text)' : 'var(--red-text)',
                  }}>
                    {testResult.ok ? (
                      <>
                        <strong>Connected — {testResult.folders.length} folder(s) found.</strong>
                        <div style={{ marginTop:6, display:'flex', flexWrap:'wrap', gap:6 }}>
                          {testResult.folders.map(f => (
                            <span key={f.path} style={{ padding:'3px 8px', borderRadius:7, background:'var(--bg3)', color:'var(--text2)' }}>
                              {f.folder} <strong style={{ color:'var(--text)' }}>{nf(f.total)}</strong>
                            </span>
                          ))}
                        </div>
                      </>
                    ) : <strong>{testResult.error}</strong>}
                  </div>
                )}

                <div style={{ display:'flex', gap:10, marginTop:14, alignItems:'center' }}>
                  <Button className="btn" type="button" onClick={handleTest} loading={busy.test}>
                    <Zap size={13}/> Test connection
                  </Button>
                  <Button className="btn primary" type="submit" loading={adding}>
                    <Plus size={13}/> {adding ? 'Connecting...' : 'Save mailbox'}
                  </Button>
                  <span style={{ fontSize:11, color:'var(--text3)' }}>
                    Auto-clean starts off — switch it on per mailbox once the counts look right.
                  </span>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Mailboxes */}
        <div className="card">
          <div className="card-head">
            <h3><Mail size={14}/> Connected mailboxes</h3>
            {boxes.length > 0 && (
              <Button className="btn sm" onClick={() => runCleanup(null)} loading={busy.all} disabled={!totals.active}>
                <Play size={12}/> Clean all now
              </Button>
            )}
          </div>

          {loading ? (
            <div style={{ padding:32, textAlign:'center', color:'var(--text3)', fontSize:12 }}>Loading...</div>
          ) : boxes.length === 0 ? (
            <div style={{ padding:32, textAlign:'center', color:'var(--text3)', fontSize:12 }}>
              No mailboxes yet — use <strong>Connect mailbox</strong> to add your first Hostinger address.
            </div>
          ) : (
            <table className="tbl">
              <thead><tr>
                <th>Mailbox</th>
                <th style={{ textAlign:'right' }}>Inbox</th>
                <th style={{ textAlign:'right' }}>Junk</th>
                <th style={{ textAlign:'right' }}>Trash</th>
                <th style={{ textAlign:'right' }}>Total</th>
                <th style={{ textAlign:'right' }}>1 · To trash</th>
                <th style={{ textAlign:'right' }}>2 · Purge</th>
                <th>Auto-clean</th>
                <th style={{ textAlign:'right' }}>Actions</th>
              </tr></thead>
              <tbody>
                {groupByDomain(boxes).flatMap(g => [
                  /* One header row per site/domain, with its own subtotal and
                     a Clean button that sweeps just that site's mailboxes. */
                  g.boxes.length > 1 || boxes.length > 4 ? (
                    <tr key={`g:${g.domain}`} style={{ background:'var(--bg3)' }}>
                      <td style={{ paddingTop:9, paddingBottom:9 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <Globe size={12} style={{ color:'var(--accent)', flexShrink:0 }}/>
                          <span className="bold" style={{ fontSize:12 }}>{g.site_name || g.domain}</span>
                          <span style={{ fontSize:10.5, color:'var(--text3)' }}>
                            {g.site_name ? `${g.domain} · ` : ''}{g.boxes.length} mailbox{g.boxes.length !== 1 ? 'es' : ''}
                          </span>
                        </div>
                      </td>
                      <td colSpan={3}></td>
                      <td style={{ textAlign:'right', color:'var(--text)', fontWeight:700 }}>{nf(g.total)}</td>
                      <td style={{ textAlign:'right', color:'var(--amber-text)', fontWeight:700 }}>{nf(g.to_trash)}</td>
                      <td style={{ textAlign:'right', color:'var(--red-text)', fontWeight:700 }}>{nf(g.to_purge)}</td>
                      <td style={{ fontSize:11, color:'var(--text3)' }}>{g.active}/{g.boxes.length} on</td>
                      <td>
                        <div style={{ display:'flex', justifyContent:'flex-end' }}>
                          <Button className="btn sm" onClick={() => runCleanup(g)}
                            loading={busy[`g:${g.domain}`]} disabled={!g.active}>
                            <Play size={11}/> Clean site
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : null,
                  ...g.boxes.map(mb => (
                  <tr key={mb.id}>
                    <td style={{ paddingLeft: 30 }}>
                      <div className="bold" style={{ fontSize:12.5 }}>{mb.email}</div>
                      <div style={{ fontSize:10.5, color:'var(--text3)', marginTop:2, display:'flex', alignItems:'center', gap:6 }}>
                        {mb.label ? `${mb.label} · ` : ''}{mb.imap_host}:{mb.imap_port} {statusBadge(mb)}
                      </div>
                    </td>
                    <td style={{ textAlign:'right' }}>{nf(folderCount(mb, 'inbox'))}</td>
                    <td style={{ textAlign:'right' }}>{nf(folderCount(mb, 'junk'))}</td>
                    <td style={{ textAlign:'right' }}>{nf(folderCount(mb, 'trash'))}</td>
                    <td style={{ textAlign:'right', color:'var(--text)', fontWeight:600 }}>{nf(mb.total)}</td>
                    <td style={{ textAlign:'right', color:'var(--amber-text)', fontWeight:600 }}>{nf(mb.to_trash)}</td>
                    <td style={{ textAlign:'right', color:'var(--red-text)', fontWeight:600 }}>{nf(mb.to_purge)}</td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <Toggle on={mb.auto_cleanup} onClick={() => toggleAuto(mb)} label={`Auto-clean ${mb.email}`} />
                        <span style={{ fontSize:11, color:'var(--text3)' }}>
                          {mb.auto_cleanup ? `${mb.retention_days}+${mb.trash_days}d` : 'paused'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:4, justifyContent:'flex-end' }}>
                        <Button className="btn sm" onClick={() => runCleanup(mb)} loading={busy[mb.id]}>
                          <Play size={11}/> Clean
                        </Button>
                        <Button className="btn sm danger" onClick={() => handleDelete(mb)}><Trash2 size={11}/></Button>
                      </div>
                    </td>
                  </tr>
                  )),
                ]).filter(Boolean)}
              </tbody>
            </table>
          )}
        </div>

        <div className="two-col">
          {/* Cleanup rule */}
          <div className="card">
            <div className="card-head">
              <h3><Clock size={14}/> Cleanup rule</h3>
              <span style={{ fontSize:10.5, color:'var(--text3)' }}>applies to every mailbox</span>
            </div>
            <div style={{ padding:'14px 16px 18px' }}>

              <div style={{ border:'1px solid var(--border2)', borderLeft:'3px solid var(--amber)', borderRadius:12, padding:'13px 14px', background:'var(--bg3)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                  <span className="badge amber">STEP 1</span>
                  <span style={{ fontSize:12.5, fontWeight:600, color:'var(--text)' }}>Move to Trash</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                  <label htmlFor="ret" style={{ fontSize:12, color:'var(--text2)' }}>Mail older than</label>
                  <input id="ret" type="number" min="1" value={rule.retention_days}
                    onChange={e => setRule({ ...rule, retention_days: e.target.value })}
                    style={{ width:72, padding:'8px 10px', borderRadius:9, background:'var(--bg2)', border:'1px solid var(--amber)', color:'var(--text)', fontSize:12.5 }}/>
                  <span style={{ fontSize:12, color:'var(--text2)' }}>days</span>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:7, marginTop:11 }}>
                  {SOURCE_FOLDERS.map(f => (
                    <label key={f.key} style={{
                      display:'inline-flex', alignItems:'center', gap:7, padding:'6px 10px', borderRadius:9,
                      background: rule.folders.includes(f.key) ? 'var(--accent-bg)' : 'var(--bg2)',
                      border:'1px solid var(--border2)', fontSize:11.5, cursor:'pointer',
                      color: rule.folders.includes(f.key) ? 'var(--accent-text)' : 'var(--text2)',
                    }}>
                      <input type="checkbox" checked={rule.folders.includes(f.key)} onChange={() => toggleFolder(f.key)}
                        style={{ accentColor:'var(--accent)' }}/>
                      {f.label}
                    </label>
                  ))}
                </div>
                <p style={{ margin:'10px 0 0', fontSize:11, color:'var(--text3)', lineHeight:1.5 }}>
                  Nothing is lost here — anything moved can be pulled back out of Trash from Webmail or your phone.
                </p>
              </div>

              <div style={{ marginTop:12, border:'1px solid var(--red)', borderLeft:'3px solid var(--red)', borderRadius:12, padding:'13px 14px', background:'var(--bg3)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                  <span className="badge red">STEP 2</span>
                  <span style={{ fontSize:12.5, fontWeight:600, color:'var(--text)' }}>Empty Trash for good</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                  <label htmlFor="trashd" style={{ fontSize:12, color:'var(--text2)' }}>Grace period in Trash</label>
                  <input id="trashd" type="number" min="0" value={rule.trash_days}
                    onChange={e => setRule({ ...rule, trash_days: e.target.value })}
                    style={{ width:72, padding:'8px 10px', borderRadius:9, background:'var(--bg2)', border:'1px solid var(--red)', color:'var(--text)', fontSize:12.5 }}/>
                  <span style={{ fontSize:12, color:'var(--text2)' }}>days</span>
                </div>
                <p style={{ margin:'10px 0 0', fontSize:11, color:'var(--red-text)', lineHeight:1.5 }}>
                  Permanent — expunged from the server, gone from every device. Each email lives{' '}
                  {Number(rule.retention_days) + Number(rule.trash_days)} days in total.
                </p>
              </div>

              <label style={{ display:'flex', alignItems:'center', gap:9, marginTop:12, fontSize:12, color:'var(--text2)', cursor:'pointer' }}>
                <input type="checkbox" checked={rule.keep_flagged}
                  onChange={e => setRule({ ...rule, keep_flagged: e.target.checked })} style={{ accentColor:'var(--accent)' }}/>
                Keep starred / flagged mail, whatever its age
              </label>

              <Button className="btn primary" onClick={saveRule} loading={savingRule} disabled={!boxes.length}
                style={{ marginTop:14, width:'100%', justifyContent:'center' }}>
                <Save size={13}/> Apply to all mailboxes
              </Button>
            </div>
          </div>

          {/* Schedule + history */}
          <div className="card">
            <div className="card-head">
              <h3><Play size={14}/> Schedule</h3>
              {cfg && <span className={`badge ${cfg.enabled ? 'green' : 'gray'}`}>{cfg.enabled ? 'Active' : 'Off'}</span>}
            </div>
            {cfg && (
              <div style={{ padding:'14px 16px 18px' }}>
                <div className="setting-row" style={{ borderBottom:'1px solid var(--border)' }}>
                  <div>
                    <div className="sr-label">Automatic cleanup</div>
                    <div className="sr-sub">Only mailboxes with auto-clean on are touched</div>
                  </div>
                  <Toggle on={cfg.enabled} onClick={() => saveSchedule({ enabled: !cfg.enabled })} label="Automatic cleanup" />
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:14 }}>
                  <div className="field" style={{ margin:0 }}>
                    <label htmlFor="freq">Frequency</label>
                    <select id="freq" value={cfg.frequency} onChange={e => saveSchedule({ frequency: e.target.value })}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </div>
                  <div className="field" style={{ margin:0 }}>
                    <label htmlFor="hour">Run at</label>
                    <select id="hour" value={cfg.run_hour} onChange={e => saveSchedule({ run_hour: parseInt(e.target.value, 10) })}>
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>

                {cfg.frequency === 'weekly' && (
                  <div className="field" style={{ marginTop:12, marginBottom:0 }}>
                    <label htmlFor="dow">Day</label>
                    <select id="dow" value={cfg.day_of_week} onChange={e => saveSchedule({ day_of_week: parseInt(e.target.value, 10) })}>
                      {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                    </select>
                  </div>
                )}

                <div style={{ marginTop:14, background:'var(--accent-bg)', border:'1px solid var(--accent2)', borderRadius:10, padding:'11px 12px', fontSize:11.5, color:'var(--accent-text)', lineHeight:1.55 }}>
                  {cfg.enabled
                    ? <>Next run {cfg.frequency === 'daily' ? 'tonight' : `on ${DAYS[cfg.day_of_week]}`} at {String(cfg.run_hour).padStart(2, '0')}:00 — about{' '}
                        <strong>{nf(totals.toTrash)}</strong> to Trash and <strong>{nf(totals.toPurge)}</strong> purged across {totals.active} mailbox(es).</>
                    : <>Automatic cleanup is off — nothing runs until you switch it on.</>}
                </div>

                <div style={{ fontSize:11, color:'var(--text3)', marginTop:10 }}>
                  Last run {when(cfg.last_run)} · {nf(cfg.last_moved)} moved · {nf(cfg.last_purged)} purged
                </div>

                {runs.length > 0 && (
                  <>
                    <div style={{ fontSize:10.5, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.5px', fontWeight:600, marginTop:16, marginBottom:8 }}>
                      Recent runs
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {runs.slice(0, 6).map(r => (
                        <div key={r.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:11.5 }}>
                          {r.result === 'success'
                            ? <CheckCircle2 size={13} style={{ color:'var(--green)', flexShrink:0 }}/>
                            : <AlertTriangle size={13} style={{ color:'var(--amber)', flexShrink:0 }}/>}
                          <span style={{ color:'var(--text2)', flexGrow:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {r.email || 'mailbox'} · {when(r.created_at)}
                          </span>
                          <span style={{ color:'var(--text3)', flexShrink:0 }}>
                            {r.result === 'success' ? `${nf(r.moved)} / ${nf(r.purged)}` : (r.note || 'failed')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
